import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import isTextEmpty from '@wix/da-editor-tiptap/pkg/components/utils/isEmpty';
import type { EditorState } from '@wix/da-editor-tiptap/pkg/components/Editor/types';
import { getMaxPrice } from '@wix/da-shared-react/pkg/redux/shared/selectors';
import { AnCQuestionType, AnCQuestionFieldName, ArtsAndCraftsDeviationFieldName } from './types';
import { MAX_TITLE_LENGTH, MAX_DESCRIPTION_LENGTH } from '../../constants';
import { TITLE_REGEX } from '../../fields/TitleField/validation';
import getMaxPriceForAnswerChoice from '../OriginalDeviationPopup/hooks/useMaxPriceForAnswerChoice';

const MAX_QUESTION_LENGTH = MAX_DESCRIPTION_LENGTH;
export const MIN_PRICE = 1;

export function useFirstStepValidation() {
  const { t } = useTranslation();
  const maxPrice = useSelector(getMaxPrice);
  const validationSchema = yup.object({
    [ArtsAndCraftsDeviationFieldName.TITLE]: yup
      .string()
      .required(t('deviationSubmit.validation.error.title.required'))
      .test('title', t('deviationSubmit.validation.error.title.invalid'), v => !TITLE_REGEX.test(v))
      .max(MAX_TITLE_LENGTH, t('deviationSubmit.validation.error.tooLong')),

    [ArtsAndCraftsDeviationFieldName.PRICE]: yup
      .number()
      .required(t('artsAndCraftsDeviationPopup.error.price.required'))
      .typeError(t('deviationSubmit.validation.error.type.number'))
      .min(
        MIN_PRICE,
        t('artsAndCraftsDeviationPopup.error.price.invalid', {
          min: MIN_PRICE,
          max: maxPrice,
        })
      )
      .max(
        maxPrice,
        t('artsAndCraftsDeviationPopup.error.price.invalid', {
          min: MIN_PRICE,
          max: maxPrice,
        })
      )
      .integer(t('artsAndCraftsDeviationPopup.error.price.integer')),

    [ArtsAndCraftsDeviationFieldName.CATEGORY]: yup
      .string()
      .required(t('artsAndCraftsDeviationPopup.error.category.required')),

    [ArtsAndCraftsDeviationFieldName.DESCRIPTION]: yup
      .object()
      .test('required', t('artsAndCraftsDeviationPopup.form.description.required'), value => {
        if (!value) {
          return false;
        }
        if (typeof value === 'function') {
          const editorData: EditorState = JSON.parse(value());
          if (editorData) {
            return !isTextEmpty(editorData.document);
          }
        }
        return false;
      }),

    [ArtsAndCraftsDeviationFieldName.SHIPPING_PROFILE]: yup
      .string()
      .required(t('artsAndCraftsDeviationPopup.error.shippingProfile.required')),
  });
  return validationSchema;
}

export function useQuestionFormValidation() {
  const { t } = useTranslation();
  const maxPrice = useSelector(getMaxPriceForAnswerChoice);
  const validationSchema = yup.object({
    [AnCQuestionFieldName.QUESTION_BODY]: yup
      .object()
      .test('required', t('originalDeviationPopup.error.question.required'), value => {
        if (!value) {
          return false;
        }
        if (typeof value === 'function') {
          const editorData: EditorState = JSON.parse(value());
          if (editorData) {
            return !isTextEmpty(editorData.document);
          }
        }
        return false;
      })
      .test('editor-max-length', t('originalDeviationPopup.error.question.tooLong'), value => {
        if (!value) {
          return true;
        }
        if (typeof value === 'function') {
          const v = value();
          if (v) {
            return v.length < MAX_QUESTION_LENGTH / 2;
          }
        }
        return true;
      }),
    [AnCQuestionFieldName.IS_REQUIRED]: yup.boolean(),
    [AnCQuestionFieldName.QUESTION_TYPE]: yup
      .string()
      .required(t('originalDeviationPopup.error.answerType.required')),
    [AnCQuestionFieldName.ANSWER_CHOICES]: yup.array().when(AnCQuestionFieldName.QUESTION_TYPE, {
      is: (type: AnCQuestionType) => type !== 'free_text',
      then: schema =>
        schema
          .of(
            yup.object().shape({
              answer: yup
                .string()
                .required(t('originalDeviationPopup.error.answerChoices.required')),
            })
          )
          .min(
            1,
            t('artsAndCraftsDeviationPopup.error.price.invalid', {
              min: MIN_PRICE,
              max: maxPrice,
            })
          )
          .max(
            maxPrice,
            t('artsAndCraftsDeviationPopup.error.price.invalid', {
              min: MIN_PRICE,
              max: maxPrice,
            })
          )
          .required(t('originalDeviationPopup.error.answerChoices.required')),
      otherwise: schema => schema.notRequired(),
    }),
  });
  return validationSchema;
}
